import { z } from "zod";

import { CONTRACT_STATUSES } from "./contract";

/**
 * Tüm Sözleşmeler listesi (CLOS-115 `[083]`) ve süzgeçleri (CLOS-116 `[084]`).
 *
 * ### ⚠ ARŞİV varsayılan listede YOK
 *
 * Kart: *"arşiv kayıtları UYGUN FİLTRE DIŞINDA aktif listeye karışmaz."* Arşivleme bir
 * kaydı gündemden çıkarmak demek; varsayılan liste onu göstermeye devam etseydi arşivleme
 * hiçbir şey yapmamış olurdu.
 *
 * ### ⚠ Süzgeç sunucuda uygulanıyor
 *
 * İstemci "arşivlileri gizle" deseydi, sunucu yine de gönderirdi ve arşivlenmiş bir
 * sözleşmenin varlığı ağ trafiğinden okunabilirdi. Kart: *"arşivlenmiş kayıtların varlığı
 * aktif liste SAYACINDAN veya filtre değerlerinden yetkisiz kullanıcıya sızdırılmaz."*
 */

/**
 * SIRALANABİLİR alanlar — KAPALI küme (CLOS-116 `[084]`).
 *
 * ⚠ Kart: *"sıralama yalnız izinli ve kanonik alanlarda yapılır; KULLANICIDAN GELEN HAM
 * ALAN ADI GÜVENİLİR KABUL EDİLMEZ."* Serbest bir alan adı kabul etseydik, kullanıcı
 * sıralamayı hiç göstermediğimiz bir kolona (ör. `value`) çevirerek o kolonun içeriğini
 * ikili aramayla sızdırabilirdi — sıralama, veriyi göstermeden sıralayan bir yan kanaldır.
 */
export const CONTRACT_SORT_FIELDS = ["updatedAt", "title", "status", "createdAt"] as const;
export type ContractSortField = (typeof CONTRACT_SORT_FIELDS)[number];

export const CONTRACT_SORT_FIELD_NAMES: Record<ContractSortField, string> = {
  updatedAt: "Son değişiklik",
  title: "Başlık",
  status: "Durum",
  createdAt: "Oluşturulma",
};

export const contractListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /**
   * `status` verilmezse ARŞİV HARİÇ hepsi döner. Boş bırakmak "hepsini göster" demek
   * olsaydı, kullanıcı hiçbir şey seçmeden arşivi de görür ve kartın kuralı bir
   * varsayılanla delinirdi.
   */
  status: z.enum(CONTRACT_STATUSES).optional(),

  /** Başlık VEYA referans numarasında arama; ikisi tek kutudan sorgulanıyor. */
  q: z.string().trim().max(200).optional(),
  /** Sözleşme türü — katalogdan (`config/contractMetadata.json`). */
  type: z.string().trim().max(50).optional(),
  /** Ana sorumlunun ÜYELİK kimliği; sorumluluk üyeliğe bağlı (`[074]`). */
  owner: z.uuid().optional(),
  /**
   * "Sorumlusu olduklarım" (CLOS-1126 · Halit K5): çağıranın KENDİ üyeliğinin owner + alan +
   * genel katkı satırları; kimlik istemciden GELMEZ, sunucu oturumdan çözer. Departman
   * üyeliği ya da destekleyen ekip kişiyi bu süzgece SOKMAZ.
   */
  responsible: z.enum(["me"]).optional(),
  /** Taraf adında arama — aynı sözleşmede birden çok taraf olabilir. */
  party: z.string().trim().max(200).optional(),
  /** Bitiş tarihi aralığı (dahil). Yalnız GÜN hassasiyetli bitiş tarihleri süzülüyor. */
  endFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Tarih YYYY-AA-GG olmalıdır")
    .optional(),
  endTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Tarih YYYY-AA-GG olmalıdır")
    .optional(),
  tag: z.string().trim().max(40).optional(),

  sort: z.enum(CONTRACT_SORT_FIELDS).default("updatedAt"),
  dir: z.enum(["asc", "desc"]).default("desc"),
});
export type ContractListQuery = z.infer<typeof contractListQuerySchema>;

export type ContractListItem = {
  id: string;
  title: string;
  status: string;
  contractType: string | null;
  /** Erişime göre izinli özet: ana sorumlunun adı — yoksa `null`. */
  ownerName: string | null;
  /** Sözleşmenin bitiş tarihi (varsa) — listede en çok sorulan tarih. */
  endDate: string | null;
  partyCount: number;
  updatedAt: string;
};

/**
 * SÜZGEÇ SEÇENEKLERİ ve sayaçları.
 *
 * ⚠ Kart: *"yalnız kullanıcının MEVCUT SONUÇ KÜMESİNDE görebildiği değerler filtre
 * seçeneği ve sayaç olarak gösterilir."* Bütün durumları sabit bir listeden gösterseydik,
 * hiçbir sözleşmesi olmayan bir kullanıcı bile organizasyonda hangi türlerin kullanıldığını
 * öğrenirdi — sayaç, görünmeyen kayıtların varlığını ele veren en sessiz kanaldır.
 */
export type ContractFacet = {
  value: string;
  count: number;
  /**
   * ⚠ Görünen ad SUNUCUDAN. Tür kataloğu `config/contractMetadata.json`'da; arayüz kendi
   * kod→ad tablosunu tutsaydı kataloğa eklenen bir tür ekranda ham kodla (`nda`) görünürdü
   * ve *"UI string uydurmaz"* kuralı delinirdi (`[072]` ile aynı karar).
   */
  label: string;
};

export type ContractListFacets = {
  status: ContractFacet[];
  type: ContractFacet[];
  /** Ana sorumlular — değer ÜYELİK kimliği, etiket kullanıcı adı (`[074]`). */
  owner: ContractFacet[];
};
